import { LegalShell } from '../components/LegalShell.js';

export function PrivacyPage() {
  return (
    <LegalShell
      stamp="política de privacidade"
      title="Como tratamos seus dados"
      lastUpdated="11 de maio de 2026"
      intro={
        <p>
          O SmartApply existe pra te ajudar a melhorar seu LinkedIn, não pra colecionar
          dados sobre você. Esta política descreve em linguagem direta o que coletamos, por
          quanto tempo guardamos, com base em qual hipótese da LGPD, e quais são os seus
          direitos como titular.
        </p>
      }
    >
      <h2>1. Quem é o responsável pelo tratamento</h2>
      <p>
        O SmartApply é mantido como projeto independente por <strong>Paulo Câmara</strong>,
        pessoa física, na qualidade de <strong>controlador</strong> dos dados tratados (LGPD,
        Art. 5º, VI). Os contatos pra exercício de direitos estão em{' '}
        <strong>"Como falar com a gente"</strong> no final deste documento.
      </p>
      <p>
        Por se tratar de iniciativa de pequeno porte (MVP, projeto independente), aplica-se a{' '}
        <strong>Resolução CD/ANPD nº 2/2022</strong>, que dispensa a nomeação de Encarregado
        formal. O canal de contato no item 10 abaixo cumpre o papel previsto na regulamentação
        pra esse porte de operação.
      </p>

      <h2>2. O que coletamos</h2>

      <h3>2.1. Dados de cadastro</h3>
      <p>
        Quando você cria uma conta, pedimos <strong>e-mail</strong>, <strong>nome</strong> e{' '}
        <strong>senha</strong>. A senha vai pro banco com hash criptográfico (Argon2id, estado
        da arte em 2026); não há como recuperar a senha original a partir do banco.
      </p>

      <h3>2.2. Conteúdo que você sobe</h3>
      <p>
        O <strong>PDF do seu perfil do LinkedIn</strong> é processado em memória do servidor e
        descartado quando sua sessão expira (uma hora de inatividade). O arquivo bruto não é
        salvo em banco nem em disco.
      </p>
      <p>
        As <strong>análises geradas</strong> só ficam salvas se você clicar explicitamente em
        "salvar análise". Ficam ligadas ao seu user e ninguém além de você consegue acessá-las.
        Você pode deletar a qualquer momento.
      </p>

      <h3>2.3. Chaves de API de provedores de IA (BYOK)</h3>
      <p>
        Sua chave do Google, Anthropic ou OpenAI fica apenas na memória do servidor durante a
        sessão. <strong>Nunca</strong> é gravada em banco, em log, ou em qualquer outro
        lugar. Quando a sessão expira, é descartada.
      </p>

      <h3>2.4. Metadados técnicos</h3>
      <p>
        Pra operação básica do serviço, registramos: <strong>IP de acesso</strong> (mascarado
        nos logs em 30 dias), <strong>data e hora</strong> de operações sensíveis (login,
        falha de login, voto no roadmap), <strong>user agent</strong> do navegador.
      </p>
      <p>
        <strong>Não usamos Google Analytics</strong>, Facebook Pixel, ou qualquer outro
        tracker de marketing.
      </p>

      <h2>3. Por que coletamos cada dado (bases legais da LGPD)</h2>
      <table>
        <thead>
          <tr>
            <th>Dado</th>
            <th>Finalidade</th>
            <th>Base legal (LGPD)</th>
            <th>Prazo</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>E-mail + nome</td>
            <td>Identificar você na sua conta</td>
            <td>Art. 7º, V (execução de contrato)</td>
            <td>Enquanto a conta existir</td>
          </tr>
          <tr>
            <td>Hash de senha</td>
            <td>Autenticar você no login</td>
            <td>Art. 7º, V (execução de contrato)</td>
            <td>Enquanto a conta existir</td>
          </tr>
          <tr>
            <td>PDF do perfil</td>
            <td>Gerar diagnóstico</td>
            <td>Art. 7º, V (execução de contrato)</td>
            <td>Memória, até 1h após upload</td>
          </tr>
          <tr>
            <td>Análises salvas</td>
            <td>Histórico opt-in</td>
            <td>Art. 7º, I (consentimento explícito)</td>
            <td>Até você deletar</td>
          </tr>
          <tr>
            <td>Chave de IA</td>
            <td>Chamar o provider escolhido</td>
            <td>Art. 7º, V (execução de contrato)</td>
            <td>Sessão; nunca em banco</td>
          </tr>
          <tr>
            <td>IP + user agent</td>
            <td>Segurança, anti-fraude</td>
            <td>Art. 7º, IX (legítimo interesse)</td>
            <td>30 dias nos logs</td>
          </tr>
        </tbody>
      </table>

      <h2>4. Com quem compartilhamos</h2>
      <p>
        <strong>Não vendemos seus dados.</strong> Não compartilhamos seu PDF, suas análises ou
        seu e-mail com terceiros pra fins de marketing.
      </p>
      <p>Operacionalmente, dependemos dos seguintes processadores:</p>
      <ul>
        <li>
          <strong>Provider de IA que você escolher</strong> (Google, Anthropic ou OpenAI):
          quando você roda uma análise, o conteúdo parseado do seu PDF é enviado pra IA da sua
          escolha pra gerar o diagnóstico. Cada um tem sua própria política de privacidade
          (Google, Anthropic e OpenAI declaram não treinar modelo com dados de chamadas via
          API).
        </li>
        <li>
          <strong>Provedor de envio de e-mail</strong> (Resend): usado pra enviar e-mails de
          verificação e recuperação de senha. Não tem acesso ao conteúdo dos seus PDFs.
        </li>
        <li>
          <strong>Provedor de hospedagem do banco</strong>: armazena sua conta, análises
          opt-in e configurações. Em ambiente de desenvolvimento atual, é um Postgres
          local; quando migrarmos pra produção em domínio público, anunciaremos o provedor
          aqui.
        </li>
      </ul>

      <h2>5. Seus direitos (LGPD Art. 18)</h2>
      <p>Você tem direito a, a qualquer momento:</p>
      <ul>
        <li>Confirmar que tratamos seus dados e quais</li>
        <li>Acessar uma cópia dos seus dados</li>
        <li>Corrigir dados incorretos ou desatualizados</li>
        <li>
          Anonimizar, bloquear ou eliminar dados desnecessários (na tela de Segurança da app
          tem botão de apagar tudo)
        </li>
        <li>Portar seus dados pra outro fornecedor</li>
        <li>Eliminar dados tratados com base em consentimento</li>
        <li>Saber com quem compartilhamos (lista acima)</li>
        <li>Revogar consentimento pra tratamentos opcionais</li>
      </ul>
      <p>
        A maioria desses direitos pode ser exercida diretamente pela app autenticada. Pra
        casos especiais, fale com a gente no contato abaixo. Respondemos em até 15 dias.
      </p>

      <h2>6. Segurança</h2>
      <ul>
        <li>Senhas armazenadas com Argon2id (parâmetros OWASP 2026)</li>
        <li>Sessões em cookie httpOnly assinado, com revogação no banco</li>
        <li>Proteção CSRF em todas as mutações de estado</li>
        <li>Rate limit por e-mail e por IP em login e operações sensíveis</li>
        <li>2FA opcional com TOTP e códigos de recuperação</li>
        <li>Logs com mascaramento de e-mails e nunca contêm chaves de API</li>
      </ul>
      <p>
        Nenhum sistema é 100% imune. Se você suspeita que sua conta foi comprometida, troque
        a senha e revogue suas chaves de IA nos providers correspondentes.
      </p>

      <h2>7. Cookies</h2>
      <p>
        Detalhes completos na{' '}
        <a href="#/cookies">Política de Cookies</a>. Em resumo: usamos só cookies
        essenciais pra autenticação e segurança. Sem trackers de marketing ou analytics.
      </p>

      <h2>8. Crianças e adolescentes</h2>
      <p>
        O SmartApply é direcionado a profissionais maiores de idade que usam o LinkedIn pra
        carreira. Não tratamos conscientemente dados de menores de 18 anos. Se você é
        responsável legal e identificou conta de menor sob seus cuidados, escreva pra gente
        e removemos.
      </p>

      <h2>9. Mudanças nesta política</h2>
      <p>
        Quando algo material mudar, atualizaremos a data no topo deste documento e
        avisaremos por e-mail (se você marcou conta verificada) pelo menos sete dias antes da
        mudança entrar em vigor.
      </p>

      <h2>10. Como falar com a gente</h2>
      <p>
        Canal único de contato:{' '}
        <a href="mailto:contato@smartapply.com.br">contato@smartapply.com.br</a>. Esse
        endereço atende tanto dúvidas gerais quanto pedidos relacionados a esta política e ao
        exercício de direitos previstos no Art. 18 da LGPD.
      </p>
      <p>
        Pra agilizar, comece o assunto do e-mail com <code>[LGPD]</code> quando o pedido for
        relacionado a privacidade ou direitos de titular. Respondemos em até 15 dias úteis.
      </p>

      <blockquote>
        Versão inicial. Este documento descreve com precisão o que o sistema faz hoje. Ajustes
        editoriais e adequações podem acontecer conforme o produto evolui — datas de revisão
        ficam sempre no topo deste documento.
      </blockquote>
    </LegalShell>
  );
}
